import axios, { type AxiosInstance } from 'axios';
import { registerInterceptors } from './api.interceptor';

/**
 * Single shared axios instance for the whole app.
 *
 * Why one instance:
 *   - Interceptors register once, not per call.
 *   - Cancellation, retries, base URL all centralize here.
 *   - Test mocks have a single seam.
 *
 * Why `withCredentials: true`:
 *   - The dev-mode session cookie (`ps_dev_session`) is set by the API on
 *     /api/v1/auth/dev-login. The browser only sends it back on subsequent
 *     requests when withCredentials is true.
 *   - Production Clerk auth uses Authorization: Bearer, but the cookie path
 *     also stays open for the dev path. The flag is harmless either way.
 *
 * Why a relative baseURL when VITE_API_BASE_URL is unset:
 *   - The Vite dev server proxies /api → API origin. Using a relative base
 *     means everything is same-origin in dev and cookies "just work" without
 *     SameSite=None or CORS preflights.
 *   - In production builds, set VITE_API_BASE_URL to the deployed API origin.
 */
function createApiClient(): AxiosInstance {
    const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/';
    const client = axios.create({
        baseURL,
        withCredentials: true,
        timeout: 30_000,
        headers: { 'content-type': 'application/json' },
    });
    registerInterceptors(client);
    return client;
}

export const apiClient: AxiosInstance = createApiClient();
